// core
import { NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import {
  CommonModule,
  CurrencyPipe,
  DecimalPipe,
  LowerCasePipe,
  TitleCasePipe,
  UpperCasePipe,
  DatePipe
} from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

// sdk
import { SdkModule } from '@campfire-nx/sdk';

// workspace
import { MaterialModule } from '@campfire-nx/material';
import { OpenModule } from '@campfire-nx/open';

// libraires
import { CookieService } from 'ngx-cookie-service';

// Raptor Directives
import { RaptorTextTransformDirective } from './directives/raptor-text-transform.directive';
import { RaptorAutotabDirective } from './directives/raptor-autotab.directive';
// Raptor components
import { RaptorFormComponent } from './components/raptor-form/raptor-form.component';
import { RaptorSectionComponent } from './components/raptor-section/raptor-section.component';
import { RaptorQuestionComponent } from './components/raptor-question/raptor-question.component';
import { RaptorQuestionErrorComponent } from './components/raptor-question-error/raptor-question-error.component';
import { RaptorQuestionReviewComponent } from './components/raptor-question-review/raptor-question-review.component';
import { RaptorValidationComponent } from './components/raptor-validation/raptor-validation.component';
import { RaptorSidebarComponent } from './components/raptor-sidebar/raptor-sidebar.component';
import { RaptorProgressComponent } from './components/raptor-progress/raptor-progress.component';
import { RaptorSectionDefaultComponent } from './components/layouts/raptor-section-default/raptor-section-default.component';
import { RaptorSidebarSectionDefaultComponent } from './components/layouts/raptor-sidebar-section-default/raptor-sidebar-section-default.component';
import { RaptorPageDefaultComponent } from './components/layouts/raptor-page-default/raptor-page-default.component';
import { RaptorPageCoversComponent } from './components/layouts/raptor-page-covers/raptor-page-covers.component';
import { RaptorPageHollardCarCoversComponent } from './components/layouts/raptor-page-hollard-car-covers/raptor-page-hollard-car-covers.component';
import { RaptorPageAxisTravelCoversComponent } from './components/layouts/raptor-page-axis-travel-covers/raptor-page-axis-travel-covers.component';
import { RaptorPageCollinsonCoversComponent } from './components/layouts/raptor-page-collinson-covers/raptor-page-collinson-covers.component';
import { RaptorPageOfframpComponent } from './components/layouts/raptor-page-offramp/raptor-page-offramp.component';
import { EmailQuoteInterstitialComponent } from './components/interstitials/email-quote-interstitial/email-quote-interstitial.component';
import { DefaultInterstitialComponent } from './components/interstitials/default-interstitial/default-interstitial.component';
import { AnimatedTransitionInterstitialComponent } from './components/interstitials/animated-transition-interstitial/animated-transition-interstitial.component';
import { RaptorInfoComponent } from './components/raptor-info/raptor-info.component';
import { RaptorTotalPriceComponent } from './components/raptor-total-price/raptor-total-price.component';
import { RaptorCallToActionComponent } from './components/raptor-call-to-action/raptor-call-to-action.component';
import { RaptorRepeaterQuestionComponent } from './components/raptor-repeater/raptor-repeater-question/raptor-repeater-question.component';
import { RaptorRepeaterSummaryComponent } from './components/raptor-repeater/raptor-repeater-summary/raptor-repeater-summary.component';
import { RaptorRepeaterMultipleQuestionComponent } from './components/raptor-repeater-multiple/raptor-repeater-multiple-question.component';
import { RaptorAutocompleteComponent } from './components/raptor-autocomplete/raptor-autocomplete.component';
import { RaptorAutocompleteMultipleComponent } from './components/raptor-autocomplete-multiple/raptor-autocomplete-multiple.component';
import { RaptorSectionOfframpComponent } from './components/raptor-section-offramp/raptor-section-offramp.component';
import { RaptorSectionOfframpDefaultComponent } from './components/layouts/raptor-section-offramp-default/raptor-section-offramp-default.component';
import { RaptorPaymentComponent } from './components/raptor-payment/raptor-payment.component';
import { RaptorStripeFormComponent } from './components/raptor-stripe-form/raptor-stripe-form.component';
import { RaptorGocardlessFormComponent } from './components/raptor-gocardless-form/raptor-gocardless-form.component';
import { RaptorVehicleSelectComponent } from './components/raptor-vehicle-select/raptor-vehicle-select.component';
import { RaptorMedicalComponent } from './components/raptor-medical/raptor-medical.component';
import { RaptorBannerHeaderComponent } from './components/raptor-banner-header/raptor-banner-header.component';
import { RaptorBannerFooterComponent } from './components/raptor-banner-footer/raptor-banner-footer.component';
import { RaptorMedicalInterstitialComponent } from './components/interstitials/raptor-medical-interstitial/raptor-medical-interstitial.component';
import { RaptorComponentComponent } from './components/raptor-component/raptor-component.component';
import { RaptorMobileComponent } from './components/raptor-mobile/raptor-mobile.component';
import { RaptorIncrementComponent } from './components/raptor-increment/raptor-increment.component';
import { RaptorFlowBannerComponent } from './components/raptor-flow-banner/raptor-flow-banner.component';
import { RaptorPriceCardComponent } from './components/raptor-price-card/raptor-price-card.component';

// Raptor services
import { RaptorControlService } from './services/raptor-control.service';
import { RaptorDataService } from './services/raptor-data.service';
import { RaptorPredicateService } from './services/raptor-predicate.service';
import { RaptorInterstitialService } from './services/raptor-interstitial.service';
import { RaptorAnalyticsService } from './services/raptor-analytics.service';
import { RaptorQuestionService } from './services/raptor-question.service';
import { RaptorMultichoiceService } from './services/raptor-multichoice.service';
import { RaptorStepService } from './services/raptor-step.service';
import { RaptorStepDataService } from './services/raptor-step-data.service';
import { RaptorRepeaterService } from './services/raptor-repeater.service';
import { RaptorPaymentService } from './services/raptor-payment.service';
import { RaptorPriceService } from './services/raptor-price.service';
import { RaptorFormService } from './services/raptor-form.service';
import { RaptorSurvicateService } from './services/raptor-survicate.service';
// Raptor Resources
import { RaptorApiService } from './resources/raptor-api.service';
import { RaptorActionService } from './resources/raptor-action.service';
// Raptor pipes
import { RaptorCurrencyPipe } from './pipes/raptor-currency.pipe';
import { RaptorDecimalPipe } from './pipes/raptor-decimal.pipe';
import { RaptorSanitizedHtmlPipe } from './pipes/raptor-sanitized-html.pipe';
import { RaptorOrderByPipe } from './pipes/raptor-order-by.pipe';
import { RaptorPhonePipe } from './pipes/raptor-phone.pipe';

@NgModule({
  declarations: [
    RaptorFormComponent,
    RaptorSectionComponent,
    RaptorQuestionComponent,
    RaptorQuestionReviewComponent,
    RaptorProgressComponent,
    RaptorSectionDefaultComponent,
    RaptorSidebarComponent,
    RaptorPageDefaultComponent,
    RaptorPageCoversComponent,
    RaptorPageHollardCarCoversComponent,
    RaptorPageAxisTravelCoversComponent,
    RaptorPageCollinsonCoversComponent,
    RaptorSidebarSectionDefaultComponent,
    EmailQuoteInterstitialComponent,
    DefaultInterstitialComponent,
    RaptorAutotabDirective,
    RaptorCallToActionComponent,
    RaptorInfoComponent,
    RaptorTotalPriceComponent,
    RaptorRepeaterQuestionComponent,
    RaptorRepeaterSummaryComponent,
    RaptorRepeaterMultipleQuestionComponent,
    RaptorTextTransformDirective,
    RaptorAutocompleteComponent,
    RaptorAutocompleteMultipleComponent,
    RaptorPageOfframpComponent,
    RaptorSectionOfframpComponent,
    RaptorSectionOfframpDefaultComponent,
    RaptorStripeFormComponent,
    RaptorGocardlessFormComponent,
    RaptorPaymentComponent,
    RaptorQuestionErrorComponent,
    RaptorVehicleSelectComponent,
    RaptorValidationComponent,
    RaptorSanitizedHtmlPipe,
    RaptorMedicalComponent,
    RaptorBannerHeaderComponent,
    RaptorBannerFooterComponent,
    RaptorMedicalInterstitialComponent,
    RaptorOrderByPipe,
    AnimatedTransitionInterstitialComponent,
    RaptorComponentComponent,
    RaptorMobileComponent,
    RaptorIncrementComponent,
    RaptorFlowBannerComponent,
    RaptorPriceCardComponent
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SdkModule,
    MaterialModule,
    OpenModule
  ],
  exports: [
    SdkModule,
    RaptorFormComponent,
    RaptorSectionComponent,
    RaptorQuestionComponent,
    RaptorQuestionErrorComponent,
    RaptorStripeFormComponent,
    RaptorGocardlessFormComponent,
    RaptorPaymentComponent
  ],
  providers: [
    CurrencyPipe,
    DecimalPipe,
    LowerCasePipe,
    TitleCasePipe,
    UpperCasePipe,
    DatePipe,
    CookieService,
    RaptorStepDataService,
    RaptorQuestionService,
    RaptorMultichoiceService,
    RaptorPredicateService,
    RaptorDataService,
    RaptorApiService,
    RaptorControlService,
    RaptorInterstitialService,
    RaptorActionService,
    RaptorStepService,
    RaptorAnalyticsService,
    RaptorRepeaterService,
    RaptorPaymentService,
    RaptorPriceService,
    RaptorCurrencyPipe,
    RaptorDecimalPipe,
    RaptorSanitizedHtmlPipe,
    RaptorOrderByPipe,
    RaptorFormService,
    RaptorSurvicateService,
    RaptorPhonePipe
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class RaptorModule {}
